import { localizeNumber } from "../../Helper/generalhelper";

const SCOOP_LOG_STATUSES = new Set([
  "scoop",
  "scoop update",
  "project scoop",
  "project_scoop",
  "report scoop",
]);

const HIDDEN_SCOOP_KEYS = new Set([
  "updated_date",
  "q_date",
  "base_currency_rate",
  "job_status_update",
  "job_summmeryId",
  "job_status",
]);

const JSON_COMPARE_KEYS = new Set(["source_lang", "target_lang", "price"]);

// Labels taken from the Scoop details UI. Unknown keys stay hidden.
export const SCOOP_FIELD_LABELS = {
  item_status: "Scoop Status",
  item_name: "Scoop Name",
  total_price: "Price",
  total_amount: "Amount in EUR",
  price: "Price lines",
  attached_workflow: "Add new job/workflow",
  source_lang: "Source Language",
  target_lang: "Target Language",
  project_type: "Project Type",
  project_pricelist: "Price List",
  due_date: "Due Date",
  due_time: "Due Time",
  po_number: "PO Number",
  submission_id: "Submission ID",
  item_email_subject: "Email Subject",
  project_volume: "Project volume",
  heads_up: "Upcoming",
  urgent_scoop: "Urgent",
  is_urgent_scoop: "Urgent",
  scoop_on_hold: "On Hold",
  coordinator: "Project Coordinator",
  manager: "Project Manager",
  qaSpecialist: "QA Specialist",
  subPc: "Substitute Project Coordinator",
  subPm: "Substitute Project Manager",
  subQa: "Substitute QA",
  contact_person: "Client Contact",
  place_of_delivery: "Place Of Delivery",
  accounting_inv_comment: "Accounting Comment",
  project_update_type: "Update Type",
  lqa_result: "Result",
  lqa_score: "Score",
  lqa_threshold: "Threshold",
  update_type_comment: "Update Comment",
  start_date: "Scoop start date",
  upcomingDate: "Upcoming Date",
};

const SCOOP_LOG_TYPES = new Set([
  "scoop status update",
  "item status update",
  "scoop update",
  "project scoop",
]);

export const SCOOP_CHANGED_PREVIEW_LIMIT = 4;

export function isScoopLog(logStatus, logType) {
  if (SCOOP_LOG_STATUSES.has(String(logStatus || "").trim().toLowerCase())) return true;
  return SCOOP_LOG_TYPES.has(String(logType || "").trim().toLowerCase());
}

export function buildAttachedWorkflowOptions(joboption = [], jobChainData = []) {
  const jobChainOptions = Array.isArray(jobChainData)
    ? jobChainData
        .filter(item => item && item.job_name && item.job_name !== "Unknown")
        .map(item => ({
          value: `${item.job_chain_id}-jobChain`,
          label: item.job_name,
          group: "Job Chain",
        }))
    : [];

  const jobOptions = Array.isArray(joboption)
    ? joboption.filter(Boolean).map(job => ({
        value: `${job.job_id}-job`,
        label: job.service_name,
        group: "Job",
      }))
    : [];

  return [...jobChainOptions, ...jobOptions];
}

export function resolveWorkflowLabel(value, workflowOptions = []) {
  if (isEmptyValue(value)) return "";
  const raw = String(value).trim();
  const options = Array.isArray(workflowOptions) ? workflowOptions : [];
  const exact = options.find(option => String(option?.value) === raw);
  if (exact?.label) return String(exact.label);

  const id = raw.split("-")[0];
  const suffix = raw.includes("-") ? raw.slice(raw.indexOf("-")) : "";
  const matches = options.filter(option => String(option?.value || "").split("-")[0] === id);
  if (suffix) {
    const typed = matches.find(option => String(option.value).endsWith(suffix));
    if (typed?.label) return String(typed.label);
  }
  if (matches.length === 1 && matches[0]?.label) return String(matches[0].label);

  const job = matches.find(option => String(option.value).endsWith("-job"));
  if (job?.label) return String(job.label);
  const chain = matches.find(option => String(option.value).endsWith("-jobChain"));
  if (chain?.label) return String(chain.label);
  return raw;
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

function isNumericValue(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  return /^-?\d+(\.\d+)?$/.test(trimmed);
}

function isBooleanLike(value) {
  if (typeof value === "boolean") return true;
  if (value === 0 || value === 1) return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim().toLowerCase();
  return trimmed === "0" || trimmed === "1" || trimmed === "true" || trimmed === "false";
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const trimmed = String(value).trim().toLowerCase();
  return trimmed === "1" || trimmed === "true";
}

function normalizeForCompare(value) {
  if (typeof value === "string" && looksLikeJson(value)) {
    return normalizeForCompare(parseMaybeJson(value));
  }
  if (isEmptyValue(value)) return null;
  if (isNumericValue(value)) return Number(typeof value === "string" ? value.trim() : value);
  if (isBooleanLike(value)) return toBoolean(value);
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(normalizeForCompare);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeForCompare(value[key]);
        return acc;
      }, {});
  }
  return value;
}

function canonicalizeBooleans(value) {
  if (Array.isArray(value)) return value.map(canonicalizeBooleans);
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = canonicalizeBooleans(value[key]);
      return acc;
    }, {});
  }
  if (value === 0 || value === false) return false;
  if (value === 1 || value === true) return true;
  return value;
}

export function valuesAreEquivalent(oldValue, newValue, fieldKey) {
  const shouldParseJson = JSON_COMPARE_KEYS.has(fieldKey);
  const left = shouldParseJson ? parseMaybeJson(oldValue) : oldValue;
  const right = shouldParseJson ? parseMaybeJson(newValue) : newValue;
  return (
    JSON.stringify(canonicalizeBooleans(normalizeForCompare(left))) ===
    JSON.stringify(canonicalizeBooleans(normalizeForCompare(right)))
  );
}

function formatLanguage(value) {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && parsed.sourceLang) {
    return String(parsed.sourceLang);
  }
  if (isEmptyValue(parsed)) return "";
  return typeof parsed === "string" ? parsed : String(value ?? "");
}

function formatPriceLines(value) {
  const parsed = parseMaybeJson(value);
  if (isEmptyValue(parsed)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      if (!row || typeof row !== "object") return String(row ?? "");
      const quantity = row.quantity ?? "";
      const priceUnit = row.pricelist ?? row.priceUnit ?? "";
      const unitPrice = row.itemPrice ?? row.prices ?? "";
      const prices = localizeNumber(row.itemTotal) ?? localizeNumber(row.amtSum) ?? "";
      return `Quantity: ${quantity}, Price Unit: ${priceUnit}, Unit Price: ${unitPrice}, Prices: ${prices}`;
    })
    .join("\n");
}

export function formatScoopFieldValue(fieldKey, value, statusNameMap, workflowOptions) {
  if (fieldKey === "item_status") {
    if (isEmptyValue(value)) return "";
    const id = String(value).trim();
    return statusNameMap?.[id] || id;
  }
  if (fieldKey === "attached_workflow") {
    return resolveWorkflowLabel(value, workflowOptions);
  }
  if (fieldKey === "source_lang" || fieldKey === "target_lang") {
    return formatLanguage(value);
  }
  if (fieldKey === "price") {
    return formatPriceLines(value);
  }
  if (fieldKey === "total_price" || fieldKey === "total_amount") {
    if (isEmptyValue(value)) return "";
    return String(localizeNumber(value));
  }
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function getScoopChangedFields(details, statusNameMap, workflowOptions) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  return Object.keys(changedFields)
    .filter(key => SCOOP_FIELD_LABELS[key] && !HIDDEN_SCOOP_KEYS.has(key))
    .filter(key => {
      const change = changedFields[key];
      if (!change || typeof change !== "object" || Array.isArray(change)) return false;
      if (!Object.prototype.hasOwnProperty.call(change, "old") && !Object.prototype.hasOwnProperty.call(change, "new")) {
        return false;
      }
      return !valuesAreEquivalent(change.old, change.new, key);
    })
    .map(key => ({
      key,
      label: SCOOP_FIELD_LABELS[key],
      old: formatScoopFieldValue(key, changedFields[key].old, statusNameMap, workflowOptions),
      new: formatScoopFieldValue(key, changedFields[key].new, statusNameMap, workflowOptions),
    }));
}

export function getScoopChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, SCOOP_CHANGED_PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - SCOOP_CHANGED_PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
